import { apiClient } from "../client";

export enum MessageRole {
  USER = "user",
  ASSISTANT = "assistant",
  SYSTEM = "system",
}

export enum ResponseMode {
  DATABASE = "database",
  NOT_FOUND = "not_found",
  DEFINITION = "definition",
  AGGREGATE = "aggregate",
  GENERAL = "general",
  ERROR = "error",
}

export interface ChatSite {
  id: number;
  name: string;
  iconColor: string;
  systemCode: string;
  country: string;
  state: string;
  city: string;
  street: string;
  postalCode: string;
  organizationId: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatSession {
  id: number;
  userId: number;
  organizationId: number | null;
  siteId: number | null;
  title: string;
  lastMessage?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
  site?: ChatSite | null;
}

export interface ChatMessage {
  id: number;
  sessionId: number;
  role: MessageRole;
  content: string;
  mode?: ResponseMode;
  metadata?: {
    assetId?: number;
    assetName?: string;
    questionType?: string;
    tokensUsed?: number;
    responseTime?: number;
    error?: string;
  };
  createdAt: string;
}

export interface MessagePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface SessionPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface GetSessionsResponse {
  sessions: ChatSession[];
  pagination: SessionPagination;
}

export interface GetMessagesResponse {
  messages: ChatMessage[];
  pagination: MessagePagination;
}

export interface CreateSessionResponse {
  message: string;
  session: ChatSession;
}

export interface SendMessageResponse {
  message: string;
  data?: ChatMessage;
  userMessage?: ChatMessage;
  assistantMessage?: ChatMessage;
}

export interface UpdateSessionResponse {
  message: string;
  session: ChatSession;
}

export interface DeleteSessionResponse {
  message: string;
}

export interface CreateSessionParams {
  title?: string;
  organizationId?: number;
  siteId?: number;
  alertId?: number;
}

export interface GetSessionsParams {
  organizationId?: number;
  siteId?: number;
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetMessagesParams {
  page?: number;
  limit?: number;
  organizationId?: number;
  siteId?: number;
}

export interface SendMessageBody {
  content: string;
  organizationId?: number;
  siteId?: number;
  alertId?: number;
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface AxiosErrorResponse {
  response?: { data: ApiError };
  message: string;
}

export class ChatService {
  /**
   * GET /chat/sessions
   * ?siteId=30   → only sessions for that site
   * (no siteId)  → all sessions (global + all sites)
   */
  static async getSessions(
    params: GetSessionsParams = {},
  ): Promise<GetSessionsResponse> {
    const query = new URLSearchParams();
    if (params.organizationId !== undefined)
      query.set("organizationId", String(params.organizationId));
    if (params.siteId !== undefined) query.set("siteId", String(params.siteId));
    if (params.page) query.set("page", String(params.page));
    if (params.limit) query.set("limit", String(params.limit));
    if (params.search) query.set("search", params.search);

    const qs = query.toString();
    const url = `/chat/sessions${qs ? `?${qs}` : ""}`;

    const response = await apiClient.get<GetSessionsResponse>(url);

    return response;
  }

  /**
   * POST /chat/sessions
   * Body: { title?, siteId? }
   * siteId = null / omitted → global session
   */
  static async createSession(
    params: CreateSessionParams,
  ): Promise<CreateSessionResponse> {
    return apiClient.post<CreateSessionResponse>("/chat/sessions", params);
  }

  /**
   * PATCH /chat/sessions/:sessionId
   * Body: { title }
   */
  static async updateSession(
    sessionId: number,
    title: string,
  ): Promise<UpdateSessionResponse> {
    return apiClient.patch<UpdateSessionResponse>(
      `/chat/sessions/${sessionId}`,
      { title },
    );
  }

  /**
   * DELETE /chat/sessions/:sessionId
   */
  static async deleteSession(
    sessionId: number,
  ): Promise<DeleteSessionResponse> {
    return apiClient.delete<DeleteSessionResponse>(
      `/chat/sessions/${sessionId}`,
    );
  }

  /**
   * POST /chat/sessions/:sessionId/messages
   * Body: { content, siteId? }
   * siteId in body lets the backend scope the AI response to the correct site
   */
  static async sendMessage(
    sessionId: number,
    body: SendMessageBody,
  ): Promise<SendMessageResponse> {
    return apiClient.post<SendMessageResponse>(
      `/chat/sessions/${sessionId}/messages`,
      body,
    );
  }

  /**
   * GET /chat/sessions/:sessionId/messages
   * ?page=1&limit=50&siteId=30
   * siteId used by backend to verify session belongs to that site
   */
  static async getMessages(
    sessionId: number,
    params: GetMessagesParams = {},
  ): Promise<GetMessagesResponse> {
    const query = new URLSearchParams();
    if (params.page) query.set("page", String(params.page));
    if (params.limit) query.set("limit", String(params.limit));
    if (params.organizationId !== undefined)
      query.set("organizationId", String(params.organizationId));
    if (params.siteId !== undefined) query.set("siteId", String(params.siteId));

    const qs = query.toString();
    return apiClient.get<GetMessagesResponse>(
      `/chat/sessions/${sessionId}/messages${qs ? `?${qs}` : ""}`,
    );
  }
}
